<script setup>
import { ref } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { formatShortDate } from '@/lib/utils'
import { isoRangeForPreset, savedPreset } from '@/composables/date-range-presets'
import { nextReportPeriodStart } from '@/composables/report'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'

import DateRangeNavigator from '@/components/shared/DateRangeNavigator.vue'

const props = defineProps({
  report: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['duplicated', 'cancel'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()

const preset = ref(props.report.period_preset ?? portalStore.dateRangePresetEnum.MONTH)
const range = ref(isoRangeForPreset(preset.value, nextReportPeriodStart(props.report)))
const isSaving = ref(false)

const onConfirm = async() => {
  isSaving.value = true
  const result = await accountingStore.duplicateReportAsync(props.report.id, { date_from: range.value.from, date_to: range.value.to, period_preset: savedPreset(preset.value) })
  isSaving.value = false
  emit('duplicated', result.id)
}
</script>

<template>
  <q-dialog :model-value="true" class="dialog-xxs" @hide="emit('cancel')">
    <q-card>
      <q-card-section class="row items-center q-gutter-sm">
        <q-avatar
          icon="mdi-content-copy"
          color="primary"
          text-color="white"
          size="md"
        />
        <span class="text-h6">{{ i18n.t('common.report.duplicateTitle', { name: props.report.name }) }}</span>
      </q-card-section>
      <q-card-section class="q-pt-none">
        <date-range-navigator
          :model-value="range"
          :default-preset="preset"
          @update:model-value="value => { range = value }"
          @preset="value => { preset = value }"
        />
        <div class="text-caption text-grey-7 q-mt-sm">{{ formatShortDate(range.from) }} – {{ formatShortDate(range.to) }}</div>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn flat :label="i18n.t('common.cancel')" @click="emit('cancel')" />
        <q-btn
          color="primary"
          :loading="isSaving"
          :label="i18n.t('common.report.duplicate')"
          @click="onConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
